import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {warnOnce} from 'graphql-client/warnOnce';
import BenchLink from 'link-preview/BenchLink';
import {AssayRecordOrigin} from 'results/assays/__generated/AssayRecordOrigin';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

import {
  GetAssayRecordOriginLink,
  GetAssayRecordOriginLinkVariables,
} from './__generated/GetAssayRecordOriginLink';
import {AssayRecordOriginType} from './AssayRecordConstants';

export const ASSAY_RECORD_ORIGIN_FRAGMENT: DocumentNode = gql`
  fragment AssayRecordOrigin on AssayRecordOriginNode {
    id
    type
    elnOrigin {
      entry {
        id
        editURL
        name
        review {
          id
          status
        }
      }
      tableName
    }
    analysisOrigin {
      analysis {
        id
        name
        url
      }
    }
  }
`;

export const AssayRecordOriginFragment: FragmentWrapper<AssayRecordOrigin> = {
  fragment: ASSAY_RECORD_ORIGIN_FRAGMENT,
  fragmentName: 'AssayRecordOrigin',
};

export const ASSAY_RECORD_ORIGIN_LINK_QUERY: DocumentNode = gql`
  query GetAssayRecordOriginLink($resultId: StrictString!) @skipAudit @engComponent(name: RESULTS) {
    result(id: $resultId) {
      ... on ResultNode {
        id
        origin {
          ...AssayRecordOrigin
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${ASSAY_RECORD_ORIGIN_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function missingEntryMessage(originType: AssayRecordOriginType): string {
  return originType === AssayRecordOriginType.API ? '(In Inbox)' : '(Deleted)';
}

const LOADING_TEXT = 'Loading...';

export function getAssayRecordOriginFromCache(resultId: string): AssayRecordOrigin | null {
  return readFragmentFromCache(AssayRecordOriginFragment, resultId);
}

export function getOriginText(resultId: string): string {
  const origin = getAssayRecordOriginFromCache(resultId);
  if (!origin) {
    return LOADING_TEXT;
  }

  if (!origin.elnOrigin.entry) {
    return missingEntryMessage(origin.type);
  }

  return origin.elnOrigin.entry.name;
}

function AssayRecordOriginLink({resultId}: {resultId: string}): JSX.Element {
  return (
    <BenchlingQuery<GetAssayRecordOriginLink, GetAssayRecordOriginLinkVariables>
      query={ASSAY_RECORD_ORIGIN_LINK_QUERY}
      variables={{resultId}}
      renderLoading={() => LOADING_TEXT}
    >
      {(data) => {
        if (isInaccessible(data.result)) {
          warnOnce('Did not expect inaccessible item.');
          return null;
        } else {
          const origin = data.result.origin;
          const {entry, tableName} = origin.elnOrigin;
          const analysis = origin.analysisOrigin?.analysis;

          if (entry != null) {
            return (
              <div>
                <BenchLink fileId={entry.id} href={benchlingUtil.pathToURL(entry.editURL)}>
                  <BenchlingIcon icon={FolderItemIcon.Entry} className="u-mini-spacing-right" />
                  {entry.name}
                </BenchLink>
                {tableName && <div>({tableName})</div>}
              </div>
            );
          } else if (analysis != null && BenchlingConfig.ENABLE_ANALYSIS_TOOL) {
            return (
              <div>
                <BenchLink fileId={analysis.id} href={benchlingUtil.pathToURL(analysis.url)}>
                  <BenchlingIcon icon={ModelTypeConstants.ANALYSIS} className="u-mini-spacing-right" />
                  {analysis.name}
                </BenchLink>
              </div>
            );
          } else {
            return <div>{missingEntryMessage(origin.type)}</div>;
          }
        }
      }}
    </BenchlingQuery>
  );
}

export default AssayRecordOriginLink;
